import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field, GoldButton, GhostButton, Select, inputCls } from "@/components/common";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const blank = (start) => ({ client_id: "", treatment_id: "", operator_id: "", date: start?.slice(0, 10) || "", time: start?.slice(11, 16) || "09:00", duration: 60, status: "programmato", notes: "" });

export const AppointmentDialog = ({ open, onOpenChange, initial, defaultStart, fixedClientId, onSaved, clients, treatments, operators }) => {
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    if (initial) setF({ ...initial, date: initial.start.slice(0, 10), time: initial.start.slice(11, 16) });
    else setF({ ...blank(defaultStart), client_id: fixedClientId || "" });
  }, [open, initial, defaultStart, fixedClientId]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pickTreatment = (e) => {
    const t = treatments.find((x) => x.id === e.target.value);
    setF((p) => ({ ...p, treatment_id: e.target.value, duration: t?.duration || p.duration }));
  };

  const save = async () => {
    if (!f.client_id || !f.date || !f.time) return toast.error("Seleziona cliente, data e ora");
    setSaving(true);
    const body = { client_id: f.client_id, treatment_id: f.treatment_id, operator_id: f.operator_id, start: `${f.date}T${f.time}`, duration: Number(f.duration) || 60, status: f.status, notes: f.notes };
    try {
      if (initial) await api.put(`/appointments/${initial.id}`, body);
      else await api.post("/appointments", body);
      toast.success(initial ? "Appuntamento aggiornato" : "Appuntamento creato");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare questo appuntamento?")) return;
    await api.delete(`/appointments/${initial.id}`);
    toast.success("Appuntamento eliminato");
    onOpenChange(false);
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-lg" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">{initial ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Cliente">
            <Select data-testid="appt-client-select" value={f.client_id} onChange={set("client_id")} disabled={!!fixedClientId} placeholder="Seleziona cliente"
              options={clients.map((c) => ({ value: c.id, label: `${c.last_name} ${c.first_name}` }))} />
          </Field></div>
          <Field label="Trattamento"><Select data-testid="appt-treatment-select" value={f.treatment_id} onChange={pickTreatment} placeholder="—" options={treatments.map((t) => ({ value: t.id, label: t.name }))} /></Field>
          <Field label="Operatore"><Select data-testid="appt-operator-select" value={f.operator_id} onChange={set("operator_id")} placeholder="—" options={operators.map((o) => ({ value: o.id, label: o.name }))} /></Field>
          <Field label="Data"><input type="date" className={inputCls} value={f.date} onChange={set("date")} data-testid="appt-date-input" /></Field>
          <Field label="Ora"><input type="time" step="900" className={inputCls} value={f.time} onChange={set("time")} data-testid="appt-time-input" /></Field>
          <Field label="Durata (min)"><input type="number" min="5" step="5" className={inputCls} value={f.duration} onChange={set("duration")} data-testid="appt-duration-input" /></Field>
          <Field label="Stato"><Select data-testid="appt-status-select" value={f.status} onChange={set("status")} options={[{ value: "programmato", label: "Programmato" }, { value: "completato", label: "Completato" }, { value: "annullato", label: "Annullato" }]} /></Field>
          <div className="sm:col-span-2"><Field label="Note"><textarea rows={2} className={inputCls} value={f.notes} onChange={set("notes")} data-testid="appt-notes-input" /></Field></div>
        </div>
        <div className="mt-2 flex flex-wrap justify-between gap-2">
          {initial ? <GhostButton onClick={remove} className="text-red-700" data-testid="appt-delete-button">Elimina</GhostButton> : <span />}
          {initial && <WhatsAppButton appt={{ ...initial, start: `${f.date}T${f.time}`, treatment_name: treatments.find((t) => t.id === f.treatment_id)?.name || "", operator_name: operators.find((o) => o.id === f.operator_id)?.name || "" }} onSent={onSaved} />}
          <GoldButton onClick={save} disabled={saving} data-testid="appt-save-button">{saving ? "Salvataggio…" : "Salva"}</GoldButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
